/* eslint-disable no-extra-parens */
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { smallMediaQuery, mediumMediaQuery, largeMediaQuery } from '../../common/utilities/sizeHelpers';
import { reduxConnect } from '../../common';
import { WidgetHeader } from '../common/widgetHeader.jsx';
import { topStoriesNewsRoutes, translate } from '../../common/translation/translate';
import { MainStory } from './storiesComponents/mainStory';
import { SubStoriesCardList } from './storiesComponents/subStoriesCardList';
import { ArrowIcon } from '../common/utilities/arrowIcon';
import { NewsStoriesErrorIcon } from '../common/utilities/svg/newsStoriesErrorIcon';
import { WidgetErrorUI } from '../widgetErrorUI/WidgetErrorUI';
import { isDataError } from '../common/utilities/isDataError';
import { getWidgetHeaderText } from '../../common/utilities/getWidgetHeaderUtil';
import { getNewsStories } from '../../common/actions/stories/storiesAction';

const PageIdToPathMapping = {
    'forecasts/current': 'weather-forecast/now',
    'forecasts/hourly': 'weather-forecast/hourly',
    'forecasts/daily': 'weather-forecast/10-day-weather',
    'alerts': 'alerts',
    'alerts/weather': 'alerts/weather',
    'alerts/spark': 'alerts/spark',
    'alerts/hurricane': 'alerts/hurricane',
    'alerts/fire': 'alerts/fire',
    'alerts/pollen': 'alerts/pollen',
    'alerts/airquality': 'alerts/air-quality',
    'alerts/severestormrisk': 'alerts/severe-storm-risk'
};

const mapStoreStateToProps = (storeState) => {
    const { newsStories } =  storeState;
    const appState = storeState.appState.appState || {};
    const { authId, enableSourceParameterOnStoriesLinks, pageId, secret, storiesBaseUrl, stdParameters, enableNewsLandingPageOnWeb2 } = appState;

    return {
        authId,
        enableSourceParameterOnStoriesLinks,
        pageId,
        secret,
        storiesBaseUrl,
        stdParameters,
        widgetStories: newsStories?.stories,
        lastError: newsStories?.lastError,
        enableNewsLandingPageOnWeb2
    };
};

const mapDispatchToProps = {
    getNewsStories
};

const StorySectionWidget = reduxConnect( mapStoreStateToProps, mapDispatchToProps )(({
    enableSourceParameterOnStoriesLinks,
    pageId,
    widgetStories,
    lastError,
    getNewsStories,
    enableNewsLandingPageOnWeb2
}) => {
    const source = PageIdToPathMapping[pageId];
    const storiesDataLoadError = isDataError(lastError);

    useEffect(() => {
        getNewsStories({ baseUrl: location.origin, category: 'Featured', count: 6 });
    }, []);

    const { mainStory, subStoriesList } = useMemo(() => {
        const [mainStory, ...restSubStoriesList] = widgetStories ?? []; 
        return { 
            mainStory, 
            subStoriesList: restSubStoriesList 
        }; 
    },
    [
        widgetStories?.length,
        widgetStories?.[0]?.Id,
        widgetStories?.[1]?.Id,
        widgetStories?.[2]?.Id,
        widgetStories?.[3]?.Id,
        widgetStories?.[4]?.Id
    ]
    );

    const widgetHeaderUI = (
        <WidgetHeader
            headerText={getWidgetHeaderText({ widgetTitle: translate('topStories') })}
            iconStyle={{ transform: 'rotate(-90deg)' }}>
            <WidgetHeaderArrowIcon>
                <ArrowIcon/>
            </WidgetHeaderArrowIcon>
        </WidgetHeader>
    );

    const widgetFooterUI = (
        <WidgetFooterContent>
            <WidgetHeader
                headerText={getWidgetHeaderText({ widgetTitle: translate('moreNews') })}
                headerStyle={{ borderBottom: '0px', padding: '2px 10px' }}
                iconStyle={{ marginTop: '3px', transform: 'rotate(-90deg)' }}>
                <WidgetHeaderArrowIcon>
                    <ArrowIcon />
                </WidgetHeaderArrowIcon>
            </WidgetHeader>
        </WidgetFooterContent>
    );

    return (
        <TopStoriesWidgetContainer>
            {enableNewsLandingPageOnWeb2
                ? <TopStoriesWidgetTitleLink href={'/news?page=top'} target='_self'>
                    {widgetHeaderUI}
                </TopStoriesWidgetTitleLink>
                : <TopStoriesWidgetTitleLink href={topStoriesNewsRoutes.linkToStoriesPage}>
                    {widgetHeaderUI}
                </TopStoriesWidgetTitleLink>}
            {!storiesDataLoadError ? <TopStoriesBody>
                {mainStory && subStoriesList?.length > 0
                    ? <>
                        <MainStory
                            nodeAlias={mainStory?.NodeAlias}
                            thumbnailImageLink={mainStory?.ThumbnailImageLink}
                            videoLink={mainStory?.VideoLinks}
                            storyTitle={mainStory?.Title}
                            storyText={mainStory?.Text}
                            enableSourceParameterOnStoriesLinks={enableSourceParameterOnStoriesLinks}
                            source={source}
                        />
                        <SubStoriesCardList 
                            subStories={subStoriesList} 
                            enableSourceParameterOnStoriesLinks={enableSourceParameterOnStoriesLinks} 
                            source={source}
                        />
                    </>
                    : <NewsStoriesErrorContainer>
                        <>
                            {translate('newStoriesNotAvailable')}
                            <NewsStoriesErrorIcon />
                        </>
                    </NewsStoriesErrorContainer>}
            </TopStoriesBody>: <WidgetErrorUI/>}
            {!storiesDataLoadError && (enableNewsLandingPageOnWeb2
                ? <TopStoriesWidgetFooterContainer href={'/news?page=top'} target='_self'>
                    {widgetFooterUI}
                </TopStoriesWidgetFooterContainer>
                : <TopStoriesWidgetFooterContainer href={topStoriesNewsRoutes.linkToStoriesPage}>
                    {widgetFooterUI}
                </TopStoriesWidgetFooterContainer>)}
        </TopStoriesWidgetContainer>
    );
});

StorySectionWidget.displayName = 'StorySectionWidget';

export { StorySectionWidget as default };

StorySectionWidget.propTypes = {
    widgetStories: PropTypes.array,
    lastError: PropTypes.any,
    getNewsStories: PropTypes.func
};

const TopStoriesWidgetContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    background: #ffffff;
    height: 600px;
    width: 100%;
    @media ${smallMediaQuery} {
        height: auto;
    }
`;

const TopStoriesWidgetTitleLink = styled.a`
    display: flex;
    text-decoration: none;
    padding-bottom: 4px;
    cursor: pointer;
    background: #ffffff;
    width: 100%;
    height: 37px;
    @media ${mediumMediaQuery} {
        padding-bottom: 0px;
    }
`;

const TopStoriesBody = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    height: 526px;
    width: 100%;
    background: #ffffff;
    @media ${mediumMediaQuery} {
        flex-direction: column;
        width: 100%;
        overflow-y: auto;
        overflow-x: hidden;
    }

    @media ${smallMediaQuery} {
        flex-direction: column;
        height: 1012px;
    }
`;

const WidgetFooterContent = styled.div`
    display: flex;
    width: 121px;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-end;
    margin-right: -9px;
    height: 100%;
    @media ${largeMediaQuery} {
        width: 130px;
    }
`;

const TopStoriesWidgetFooterContainer = styled.a`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    padding-right: 10px;
    text-decoration: none;
    width: 100%;
    height: 37px;
    border-top: 1px solid #eee;
`;

const NewsStoriesErrorContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-size: 23px;
    color: #000000;
    width: 100%;
    height: 100%;
`;

const WidgetHeaderArrowIcon = styled.svg`
    position: absolute;
    top: 50%;
    left: 50%;
    margin-top: -3px;
    margin-left: -3px;
    fill: #666;
    stroke-width: 1px;
    stroke: #666;
    width: 10px;
    height: 7px;
`;
